import LegalLayout, {
  LegalH2,
  LegalP,
  LegalList,
} from "@/components/LegalLayout";
import { AlertCircle } from "lucide-react";

const CONTACT_EMAIL = "hola@matronaia.com";

export default function MedicalNotice() {
  return (
    <div data-testid="medical-notice-page">
      <LegalLayout
        eyebrow="Documento esencial"
        title="Aviso Médico"
        lastUpdated="1 de julio de 2026"
      >
        <div
          className="rounded-2xl border border-[#d4909e]/40 bg-[#f4dee2]/35 p-6 sm:p-7 flex gap-4 items-start"
          data-testid="medical-warning-box"
        >
          <AlertCircle
            className="text-[#b7677a] flex-shrink-0 mt-0.5"
            size={22}
            strokeWidth={1.6}
          />
          <p className="text-[#3c3531] text-[0.98rem] leading-relaxed m-0">
            <strong className="font-medium text-[#b7677a]">
              Matrona IA no sustituye
            </strong>{" "}
            la valoración de una matrona, ginecóloga, obstetra, pediatra,
            médico de familia, enfermera, psicóloga u otro profesional
            sanitario cualificado. La información ofrecida es exclusivamente
            informativa, educativa y de acompañamiento.
          </p>
        </div>

        <LegalP>
          Matrona IA es una aplicación de acompañamiento, orientación e
          información educativa sobre embarazo, parto, posparto, lactancia,
          crianza, duelo perinatal y salud maternoinfantil.
        </LegalP>

        <LegalP>
          La información ofrecida por Matrona IA tiene finalidad exclusivamente
          informativa, educativa y de apoyo. No constituye diagnóstico médico,
          tratamiento sanitario, prescripción, indicación clínica individualizada
          ni sustitución de la atención profesional.
        </LegalP>

        <LegalP>
          Matrona IA no es un servicio sanitario, médico, psicológico ni de
          urgencias.
        </LegalP>

        <LegalH2>Uso de la inteligencia artificial</LegalH2>
        <LegalP>
          Matrona IA utiliza sistemas de inteligencia artificial para generar
          respuestas orientativas basadas en la información proporcionada por la
          usuaria.
        </LegalP>
        <LegalP>
          Aunque el objetivo es ofrecer información útil, respetuosa y basada en
          evidencia, las respuestas pueden contener errores, limitaciones o no
          adaptarse completamente a una situación clínica concreta.
        </LegalP>
        <LegalP>
          La usuaria no debe tomar decisiones médicas importantes basándose
          únicamente en la información proporcionada por la aplicación.
        </LegalP>

        <LegalH2>Situaciones de urgencia</LegalH2>
        <LegalP>
          Matrona IA no está diseñada para atender urgencias médicas.
        </LegalP>
        <LegalP>
          En caso de emergencia, síntomas graves o preocupación importante por
          la salud de la madre o del bebé, la usuaria debe contactar con los
          servicios sanitarios correspondientes, acudir a urgencias o llamar al
          112.
        </LegalP>
        <LegalP>
          Entre otros casos, se recomienda buscar atención sanitaria urgente si
          hay:
        </LegalP>
        <LegalList
          items={[
            "Sangrado abundante durante el embarazo o posparto.",
            "Dolor intenso o persistente.",
            "Fiebre alta o mal estado general.",
            "Dificultad respiratoria.",
            "Convulsiones.",
            "Pérdida de conciencia.",
            "Disminución o ausencia de movimientos fetales.",
            "Contracciones prematuras.",
            "Pérdida de líquido amniótico antes de tiempo.",
            "Dolor de cabeza intenso, alteraciones visuales o hinchazón brusca.",
            "Ideas de hacerse daño, hacer daño al bebé o sensación de no poder continuar.",
            "Recién nacido con fiebre, dificultad respiratoria, rechazo persistente de tomas, somnolencia extrema, coloración azulada o signos de deshidratación.",
          ]}
        />
        <LegalP>
          Esta lista no es exhaustiva. Ante cualquier duda importante, debe
          consultarse con un profesional sanitario.
        </LegalP>

        <LegalH2>Embarazo, parto y posparto</LegalH2>
        <LegalP>
          Las recomendaciones durante el embarazo, parto y posparto pueden variar
          según antecedentes, factores de riesgo, evolución clínica, protocolos
          locales y criterio profesional.
        </LegalP>
        <LegalP>
          Matrona IA puede ayudar a comprender opciones, preparar preguntas,
          conocer derechos, identificar señales de alarma y acompañar
          emocionalmente, pero no puede valorar de forma completa la situación
          clínica de una mujer o un bebé.
        </LegalP>

        <LegalH2>Lactancia y recién nacido</LegalH2>
        <LegalP>
          La información sobre lactancia, alimentación del bebé, sueño,
          deposiciones, piel, vacunas o cuidados del recién nacido es
          orientativa.
        </LegalP>
        <LegalP>
          Ante pérdida de peso importante, signos de deshidratación, fiebre,
          decaimiento, dificultad respiratoria, rechazo de tomas, ictericia
          intensa, sangre persistente en heces o preocupación por el estado del
          bebé, debe consultarse con una matrona, pediatra u otro profesional
          sanitario.
        </LegalP>

        <LegalH2>Análisis de imágenes</LegalH2>
        <LegalP>
          Matrona IA puede permitir que la usuaria suba imágenes para recibir
          orientación informativa.
        </LegalP>
        <LegalP>
          El análisis de imágenes mediante inteligencia artificial tiene
          limitaciones y no sustituye una exploración física, una prueba
          diagnóstica ni la valoración de un profesional sanitario.
        </LegalP>
        <LegalP>
          Las imágenes no deben utilizarse como único criterio para tomar
          decisiones médicas.
        </LegalP>

        <LegalH2>Medicamentos y tratamientos</LegalH2>
        <LegalP>
          Matrona IA no prescribe medicamentos, tratamientos ni dosis
          individualizadas.
        </LegalP>
        <LegalP>
          Cualquier decisión sobre medicamentos durante embarazo, lactancia,
          posparto o en bebés debe confirmarse con un profesional sanitario
          cualificado.
        </LegalP>

        <LegalH2>Responsabilidad de la usuaria</LegalH2>
        <LegalP>
          La usuaria es responsable de decidir cuándo consultar con profesionales
          sanitarios y de no retrasar la atención médica en situaciones que
          puedan requerir valoración urgente o presencial.
        </LegalP>
        <LegalP>
          Matrona IA está diseñada para acompañar, informar y orientar, pero no
          para sustituir el juicio clínico profesional.
        </LegalP>

        <LegalH2>Actualización de la información</LegalH2>
        <LegalP>
          Matrona IA intenta ofrecer información actualizada y basada en
          evidencia científica. Sin embargo, la evidencia, las guías clínicas y
          las recomendaciones sanitarias pueden cambiar con el tiempo.
        </LegalP>
        <LegalP>
          Por ello, la información ofrecida debe entenderse como apoyo educativo
          y no como una indicación médica definitiva.
        </LegalP>

        <LegalH2>Contacto</LegalH2>
        <LegalP>
          Para dudas relacionadas con este aviso médico, puedes escribir a{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-[#b7677a] underline underline-offset-4 decoration-[#f1c8d0]"
          >
            {CONTACT_EMAIL}
          </a>
          .
        </LegalP>

        <div className="mt-14 pt-10 border-t border-[#e8dcc8]/70 text-center">
          <p className="font-serif italic text-xl sm:text-2xl text-[#b7677a] leading-snug">
            “Matrona IA nace para acompañar, informar y orientar.
            <br />
            Nunca para sustituir el cuidado humano, la relación clínica ni la
            autonomía de cada mujer.”
          </p>
        </div>
      </LegalLayout>
    </div>
  );
}